import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { SocialProofStackedAvatar } from './SocialProofStackedAvatar';
import { StackItem } from './StackedAvatars';
import { Analytics } from '../../../utils';
import {
  analyticsEventActions,
  analyticsEventCategory,
} from '../../../utils/constants';
import { features } from '../../../../shared/features';
import { Logger } from '../../../../shared/logger';
import { CompactActionBarV2 } from './CompactActionBarV2';

const SocialProofDynamicMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background-color: #ffffff;
  width: max-content;
  max-width: 88vw;
  border-radius: 0.625rem;
  padding: 0.5rem;
  cursor: pointer;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
`;

const StyledSocialProofContainer = styled.div<{
  $position?: 'left' | 'right';
}>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: ${({ $position }) =>
    $position === 'left' ? 'flex-start' : 'flex-end'};
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 2px;
  gap: 0.25rem;

  &:hover,
  &:focus,
  &:focus-within {
    ${SocialProofDynamicMessageContainer} {
      background-color: #f5f4f2;
    }

    ${StackItem} {
      margin-left: -0.35rem;
    }

    ${({ $position }) =>
      $position === 'left' &&
      css`
        ${StackItem}:first-child {
          margin-left: 0;
        }
      `}
  }
`;

const StyledSocialProofCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
  width: 100%;
  align-items: inherit;
`;

const Title = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #232323;
  display: flex;
  gap: 0.5rem;
  white-space: nowrap;
`;

const Subtitle = styled.span`
  font-size: 0.75rem;
  color: #79716b;
  font-weight: 400;
  white-space: nowrap;
`;

const Wave = styled.span`
  font-size: 1.35rem;
  line-height: 1;
  display: inline-flex;
`;

function getSubtitle(count: number) {
  /**
   * Always return this until social proof is built and we can return the message with a dynamic count
  // return `${count} people messaged recently`;
   * 
   */
  return "Learn what it's like to live here";
}

export function SocialProofContainer() {
  const residentsCount = useMemo(() => globalThis.xprops.residents.length, []);
  const { onToggleMessenger, onReadyDispatcher } = globalThis.xprops;

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      try {
        onToggleMessenger(features.MULTI_MESSAGE, true);
      } catch (error) {
        Logger.error(
          `failed to execute keyboard exec onToggleMessenger: ${error}`
        );
      }
    }
  };

  const onClickHandler = useCallback(async () => {
    try {
      onToggleMessenger(features.MULTI_MESSAGE, true);
    } catch (error) {
      Logger.error(`failed to execute onToggleMessenger: ${error}`);
    } finally {
      Analytics.event({
        category: analyticsEventCategory.compact,
        action: analyticsEventActions.opens_chat_with_resident,
      });
    }
  }, [onToggleMessenger]);

  useEffect(() => {
    Analytics.event({
      category: analyticsEventCategory.compact,
      action: analyticsEventActions.loads_compact_view,
    });
  }, []);

  useLayoutEffect(() => {
    onReadyDispatcher();
  }, [onReadyDispatcher]);

  return (
    <StyledSocialProofContainer
      $position={globalThis.xprops.listing.position}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="button"
    >
      <StyledSocialProofCard onClick={onClickHandler}>
        <SocialProofStackedAvatar
          residents={globalThis.xprops.residents}
          totalResidents={globalThis.xprops.residents.length}
          position={globalThis.xprops.listing.position}
        />
        <SocialProofDynamicMessageContainer>
          <Title>
            <Wave role="img" aria-hidden="true">
              👋
            </Wave>
            {` Ask a real resident`}
          </Title>
          {residentsCount ? (
            <Subtitle>{getSubtitle(residentsCount)}</Subtitle>
          ) : null}
        </SocialProofDynamicMessageContainer>
      </StyledSocialProofCard>
      <CompactActionBarV2 />
    </StyledSocialProofContainer>
  );
}
